// src/components/common/NotificationBadge.js
import React, { useState, useRef, useEffect } from 'react';
import { BellIcon, EnvelopeIcon, CheckCircleIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const NotificationBadge = () => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Mock Notifications Data
    const [notifications, setNotifications] = useState([
        {
            id: 'notif-001',
            type: 'customer_enrollment',
            message: 'New customer **Sarah Johnson** enrolled in your Gold Tier!',
            timestamp: '5 minutes ago',
            read: false,
            link: '/customers/sarah-johnson-id' // Example link
        },
        {
            id: 'notif-002',
            type: 'promotion_started',
            message: 'Promotion **"Summer Sale 2025"** has started.',
            timestamp: '1 hour ago',
            read: false,
            link: '/promotions/summer-sale-id'
        },
        {
            id: 'notif-003',
            type: 'loyalty_program_update',
            message: 'Loyalty Program **"VIP Rewards"** points earning rule updated.',
            timestamp: 'Yesterday',
            read: true,
            link: '/settings/loyalty-program'
        },
        {
            id: 'notif-004',
            type: 'system_alert',
            message: 'System maintenance scheduled for June 10th at 2 AM SAST.',
            timestamp: '2 days ago',
            read: false,
            link: '/status'
        },
        {
            id: 'notif-005',
            type: 'communication_sent',
            message: 'Email "Welcome New Members" successfully sent to 1,200 customers.',
            timestamp: '3 days ago',
            read: true,
            link: '/communications/msg-001'
        },
    ]);

    const unreadCount = notifications.filter(n => !n.read).length;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    const handleNotificationClick = (id) => {
        setNotifications(notifications.map(n =>
            n.id === id ? { ...n, read: true } : n
        ));
        // In a real app, you might also navigate to n.link or open a modal
    };

    const handleMarkAllAsRead = () => {
        setNotifications(notifications.map(n => ({ ...n, read: true })));
    };

    const handleClearNotification = (id) => {
        setNotifications(notifications.filter(n => n.id !== id));
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'customer_enrollment': return <UserPlusIcon className="h-5 w-5 text-indigo-500" />;
            case 'promotion_started': return <EnvelopeIcon className="h-5 w-5 text-green-500" />;
            case 'loyalty_program_update': return <CheckCircleIcon className="h-5 w-5 text-blue-500" />;
            case 'system_alert': return <BellIcon className="h-5 w-5 text-red-500" />;
            case 'communication_sent': return <EnvelopeIcon className="h-5 w-5 text-purple-500" />;
            default: return <BellIcon className="h-5 w-5 text-gray-500" />;
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="relative p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                aria-label="Notifications"
            >
                <BellIcon className="h-6 w-6" />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full transform translate-x-1/2 -translate-y-1/2">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
                )}
            </button>

            {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 z-50 transform origin-top-right animate-fade-in-down">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllAsRead}
                                className="text-sm text-indigo-600 hover:text-indigo-800"
                            >
                                Mark All As Read
                            </button>
                        )}
                    </div>

                    <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <li className="p-4 text-center text-gray-500 text-sm">
                                No new notifications.
                            </li>
                        ) : (
                            notifications.map((notif) => (
                                <li
                                    key={notif.id}
                                    className={`relative flex items-start p-4 ${notif.read ? 'bg-white' : 'bg-indigo-50'} hover:bg-gray-50 cursor-pointer`}
                                    onClick={() => handleNotificationClick(notif.id)}
                                >
                                    {!notif.read && (
                                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-indigo-600 ml-2"></span>
                                    )}
                                    <div className={`flex-shrink-0 ${!notif.read ? 'ml-4' : 'ml-2'} mr-3`}>
                                        {getNotificationIcon(notif.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm ${notif.read ? 'text-gray-600' : 'text-gray-800 font-medium'}`}
                                           dangerouslySetInnerHTML={{ __html: notif.message }} />
                                        <p className="text-xs text-gray-500 mt-1">{notif.timestamp}</p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleClearNotification(notif.id); }}
                                        className="flex-shrink-0 ml-3 text-gray-400 hover:text-red-500 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                        aria-label="Clear notification"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>

                    <div className="p-3 border-t border-gray-200 text-center">
                        <a
                            href="/notifications" // Link to a dedicated Notifications page (if it exists)
                            className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
                            onClick={() => setIsDropdownOpen(false)} // Close dropdown on navigation
                        >
                            View All Notifications
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationBadge;